import React, { ReactNode } from "react"
import { ChevronDownIcon } from "lucide-react"

import {
  TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
  TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS,
} from "@/components/trade-filter-selects"
import { Card, CardAction, CardContent, CardHeader } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext"
import {
  dashboardChartCardClassName,
  dashboardChartCardContentClassName,
  dashboardChartCardHeaderClassName,
  dashboardMutedTextClassName,
} from "./dashboardChartCardShell"

const ArrowUpRight: React.FC<{ color: string; size?: number }> = ({ color, size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke={color}
    width={size}
    height={size}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
    />
  </svg>
)

const ArrowDownRight: React.FC<{ color: string; size?: number }> = ({ color, size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke={color}
    width={size}
    height={size}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6 9 12.75l4.286-4.286a11.948 11.948 0 0 1 4.306 6.43l.776 2.898m0 0 3.182-5.511m-3.182 5.51-5.511-3.181"
    />
  </svg>
)

interface ViewOptions {
  portfolioValue: boolean
  tradingPnlOnly: boolean
  transfers: boolean
  averageWinRate?: boolean
}

interface ChartCardProps {
  title: string
  percentage: number
  chart: ReactNode
  portfolioSize?: number
  value?: number
  valueFormatter?: (value: number) => string
  timeframe?: string
  onTimeframeChange?: (timeframe: string) => void
  viewOptions?: ViewOptions
  onViewOptionsChange?: (options: ViewOptions) => void
  showViewSelector?: boolean
  /** Optional control in the header's top-right (e.g. a Combined / Longs / Shorts toggle). */
  headerAction?: ReactNode
  /** Optional line under the value (e.g. where the account started, deepest dip). */
  subtitle?: ReactNode
}

const greenColor = "#06E363"
const redColor = "#fc8181"

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  percentage,
  chart,
  portfolioSize,
  value,
  valueFormatter,
  timeframe = "all-time",
  onTimeframeChange,
  viewOptions,
  onViewOptionsChange,
  showViewSelector = true,
  headerAction,
  subtitle,
}) => {
  const { formatMoney } = useDisplayCurrency()

  const isWinRateView =
    viewOptions?.averageWinRate !== undefined &&
    viewOptions.portfolioValue === true &&
    viewOptions.tradingPnlOnly === false &&
    viewOptions.transfers === false

  return (
    <Card
      data-statistics-shadcn-card
      data-dashboard-sidebar-surface-card=""
      className={dashboardChartCardClassName}
    >
      <CardHeader className={dashboardChartCardHeaderClassName}>
        <div className="flex w-full min-w-0 flex-col self-start">
          <div className={cn("mb-1.5 text-sm font-medium uppercase font-[family-name:var(--font-primary)]", dashboardMutedTextClassName)}>
            {title}
          </div>
          {(portfolioSize !== undefined || value !== undefined) && (
            <div className="mb-1.5 flex items-baseline gap-2">
              <div className={cn("text-3xl font-semibold sm:text-4xl md:text-5xl font-[family-name:'IBM_Plex_Sans',sans-serif]", dashboardMutedTextClassName)}>
                {value !== undefined && valueFormatter
                  ? valueFormatter(value)
                  : portfolioSize !== undefined
                    ? formatMoney(portfolioSize)
                    : ""}
              </div>
              <div className="flex items-center gap-1">
                <span
                  className={cn(
                    "text-base font-bold font-[family-name:'IBM_Plex_Sans',sans-serif]",
                    percentage > 0 ? "text-[#06E363]" : "text-red-400"
                  )}
                >
                  {typeof percentage === "number" ? Math.abs(percentage).toFixed(2) : "0.00"}%
                </span>
                {percentage > 0 ? (
                  <ArrowUpRight color={greenColor} size={16} />
                ) : (
                  <ArrowDownRight color={redColor} size={16} />
                )}
              </div>
            </div>
          )}
          {subtitle ? (
            <div className={cn("text-xs font-[family-name:var(--font-primary)]", dashboardMutedTextClassName)}>{subtitle}</div>
          ) : null}
        </div>
        {(headerAction || onTimeframeChange || (showViewSelector && viewOptions && onViewOptionsChange)) ? (
          <CardAction>
            <div className="flex items-center gap-3">
              {headerAction}
              {onTimeframeChange ? (
                <select
                  value={timeframe}
                  onChange={(e) => onTimeframeChange(e.target.value)}
                  className={cn("w-fit min-w-0 cursor-pointer appearance-none border-0 bg-transparent pr-4 text-[10px] font-medium uppercase outline-none sm:text-[11px] md:text-xs font-[family-name:'IBM_Plex_Sans',sans-serif] focus:ring-0", dashboardMutedTextClassName)}
                  aria-label="Chart timeframe"
                >
                  <option value="today">Today</option>
                  <option value="7-days">7 days</option>
                  <option value="14-days">14 days</option>
                  <option value="30-days">30 days</option>
                  <option value="all-time">All time</option>
                </select>
              ) : null}
              {showViewSelector && viewOptions && onViewOptionsChange ? (
                <div
                  data-field-select=""
                  data-trade-log-columns-control=""
                  data-dashboard-chart-views-control=""
                  data-dashboard-header-field-select=""
                  className="w-fit shrink-0"
                >
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        data-slot="select-trigger"
                        data-size="default"
                        data-trade-log-columns-trigger=""
                        className={TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS}
                      >
                        <span data-slot="select-value">Views</span>
                        <ChevronDownIcon className="size-4 shrink-0 text-current" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      side="bottom"
                      avoidCollisions={false}
                      className="w-auto min-w-40"
                      data-trade-log-columns-menu=""
                      data-field-select-menu=""
                      data-dashboard-chart-views-menu=""
                    >
                      {isWinRateView ? (
                        <DropdownMenuCheckboxItem
                          className={TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS}
                          checked={!!viewOptions.averageWinRate}
                          onCheckedChange={(checked) => {
                            onViewOptionsChange({
                              ...viewOptions,
                              averageWinRate: !!checked,
                            })
                          }}
                        >
                          <span
                            data-dashboard-chart-views-option=""
                            data-series-tone="cyan"
                            className="text-[#38BBD1]"
                          >
                            Average Win Rate
                          </span>
                        </DropdownMenuCheckboxItem>
                      ) : (
                        <>
                          <DropdownMenuCheckboxItem
                            className={TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS}
                            checked={!!viewOptions.tradingPnlOnly}
                            onCheckedChange={(checked) => {
                              onViewOptionsChange({
                                ...viewOptions,
                                tradingPnlOnly: !!checked,
                              })
                            }}
                          >
                            <span
                              data-dashboard-chart-views-option=""
                              data-series-tone="cyan"
                              className="text-[#38BBD1]"
                            >
                              Trading P&L Only
                            </span>
                          </DropdownMenuCheckboxItem>
                          <DropdownMenuCheckboxItem
                            className={TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS}
                            checked={!!viewOptions.transfers}
                            onCheckedChange={(checked) => {
                              onViewOptionsChange({
                                ...viewOptions,
                                transfers: !!checked,
                              })
                            }}
                          >
                            <span
                              data-dashboard-chart-views-option=""
                              data-series-tone="orange"
                              className="text-[#F49113]"
                            >
                              Transfers
                            </span>
                          </DropdownMenuCheckboxItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : null}
            </div>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className={dashboardChartCardContentClassName}>
        {chart ?? null}
      </CardContent>
    </Card>
  )
}

export default ChartCard
